import { Component, OnInit, signal, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faLanguage,
  faCheck,
  faExclamationTriangle,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { TranslateModule } from '@ngx-translate/core';
import {
  TranslationLanguage,
  LanguageConfig,
  TRANSLATION_LANGUAGES,
  TranslationProgress,
  TranslationStatus,
} from '../../types/translation.types';

/**
 * Modal data interface expected from parent component
 */
export interface ModalTranslateLanguagesData {
  /** Current source language */
  sourceLanguage: TranslationLanguage;
  /** Languages that already have content (for warning badges) */
  languagesWithContent: TranslationLanguage[];
  /** Optional: Pre-selected languages */
  preSelectedLanguages?: TranslationLanguage[];
  /** Whether to show progress indicators */
  showProgress?: boolean;
  /** Current translation progress by language */
  translationProgress?: TranslationProgress[];
}

/**
 * Modal result returned to parent component
 */
export interface ModalTranslateLanguagesResult {
  /** Whether the user confirmed the translation */
  confirmed: boolean;
  /** Selected target languages */
  selectedLanguages: TranslationLanguage[];
  /** Whether user confirmed to overwrite existing content */
  overwriteConfirmed: boolean;
}

/**
 * Modal component for selecting target languages for translation.
 *
 * Features:
 * - Checkboxes for each available language (excluding source)
 * - "Has content" badges for languages with existing translations
 * - Warning when overwriting existing content
 * - Translation progress indicators (optional)
 * - Select all / Deselect all functionality
 */
@Component({
  selector: 'app-modal-translate-languages',
  standalone: true,
  imports: [FormsModule, FontAwesomeModule, TranslateModule],
  template: `
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <fa-icon [icon]="faLanguage" class="mr-2"></fa-icon>
          {{ 'Select languages for translation' | translate }}
        </h5>
        <button
          type="button"
          class="close"
          (click)="onCancel()"
          [disabled]="isTranslating()"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <div class="modal-body">
        <!-- Source language info -->
        <div class="alert alert-info mb-3 py-2">
          <small>
            {{ 'Translating from' | translate }}:
            <strong>{{ getLanguageName(data.sourceLanguage) }}</strong>
          </small>
        </div>

        <!-- Overwrite warning -->
        @if (hasSelectedLanguagesWithContent()) {
        <div class="alert alert-warning mb-3">
          <fa-icon [icon]="faExclamationTriangle" class="mr-2"></fa-icon>
          <span>
            {{
              'The following languages already have content and will be overwritten:'
                | translate
            }}
            <strong>{{ getSelectedLanguagesWithContentNames() }}</strong>
          </span>
        </div>
        }

        <!-- Select all toggle -->
        <div class="mb-3 d-flex justify-content-between align-items-center">
          <div class="custom-control custom-checkbox">
            <input
              type="checkbox"
              class="custom-control-input"
              id="select-all"
              [checked]="allSelected()"
              [indeterminate]="someSelected() && !allSelected()"
              (change)="toggleSelectAll()"
              [disabled]="isTranslating()"
            />
            <label class="custom-control-label font-weight-bold" for="select-all">
              {{ 'Select all' | translate }}
            </label>
          </div>
          <small class="text-muted">
            {{ selectedCount() }} {{ 'of' | translate }}
            {{ availableLanguages().length }} {{ 'selected' | translate }}
          </small>
        </div>

        <hr class="my-2" />

        <!-- Language checkboxes -->
        <div class="language-list">
          @for (lang of availableLanguages(); track lang.code; let i = $index) {
          <div
            class="language-item d-flex align-items-center py-2"
            [class.has-content]="hasExistingContent(lang.code)"
          >
            <div class="custom-control custom-checkbox flex-grow-1">
              <input
                type="checkbox"
                class="custom-control-input"
                [id]="'lang-' + lang.code"
                [checked]="isSelected(lang.code)"
                (change)="toggleLanguage(lang.code)"
                [disabled]="isTranslating()"
              />
              <label
                class="custom-control-label d-flex align-items-center"
                [for]="'lang-' + lang.code"
              >
                <span
                  class="flag-icon mr-2"
                  [class]="'flag-icon-' + lang.flag"
                ></span>
                <span class="language-name">{{ lang.name }}</span>
              </label>
            </div>

            <!-- Status indicators -->
            <div class="status-indicators d-flex align-items-center gap-2">
              <!-- Has content badge -->
              @if (hasExistingContent(lang.code)) {
              <span
                class="badge badge-secondary"
                [title]="'This language already has content' | translate"
              >
                {{ 'Has content' | translate }}
              </span>
              }

              <!-- Translation progress -->
              @if (getTranslationStatus(lang.code); as status) {
              <span class="translation-status">
                @if (status === 'loading') {
                <fa-icon
                  [icon]="faSpinner"
                  animation="spin"
                  class="text-primary"
                ></fa-icon>
                }
                @if (status === 'success') {
                <fa-icon
                  [icon]="faCheck"
                  class="text-success"
                ></fa-icon>
                }
                @if (status === 'error') {
                <fa-icon
                  [icon]="faExclamationTriangle"
                  class="text-danger"
                ></fa-icon>
                }
              </span>
              }
            </div>
          </div>
          }
        </div>
      </div>

      <div class="modal-footer">
        <div class="btn-toolbar flex-grow-1 justify-content-between">
          <button
            type="button"
            class="btn btn-secondary"
            (click)="onCancel()"
            [disabled]="isTranslating()"
          >
            {{ 'Cancel' | translate }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            (click)="onConfirm()"
            [disabled]="selectedCount() === 0 || isTranslating()"
          >
            @if (isTranslating()) {
            <fa-icon
              [icon]="faSpinner"
              animation="spin"
              class="mr-2"
            ></fa-icon>
            }
            {{
              isTranslating()
                ? ('Translating...' | translate)
                : ('Translate' | translate)
            }}
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .language-list {
        max-height: 300px;
        overflow-y: auto;
      }

      .language-item {
        border-bottom: 1px solid #eee;
      }

      .language-item:last-child {
        border-bottom: none;
      }

      .language-item.has-content {
        background-color: #fffbeb;
      }

      .flag-icon {
        width: 20px;
        height: 15px;
        display: inline-block;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
      }

      /* Flag backgrounds using country codes */
      .flag-icon-nl {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 9 6'%3E%3Crect fill='%2321468B' width='9' height='6'/%3E%3Crect fill='white' width='9' height='4'/%3E%3Crect fill='%23AE1C28' width='9' height='2'/%3E%3C/svg%3E");
      }

      .flag-icon-gb {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 30'%3E%3CclipPath id='t'%3E%3Cpath d='M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z'/%3E%3C/clipPath%3E%3Cpath d='M0,0 v30 h60 v-30 z' fill='%2300247d'/%3E%3Cpath d='M0,0 L60,30 M60,0 L0,30' stroke='%23fff' stroke-width='6'/%3E%3Cpath d='M0,0 L60,30 M60,0 L0,30' clip-path='url(%23t)' stroke='%23cf142b' stroke-width='4'/%3E%3Cpath d='M30,0 v30 M0,15 h60' stroke='%23fff' stroke-width='10'/%3E%3Cpath d='M30,0 v30 M0,15 h60' stroke='%23cf142b' stroke-width='6'/%3E%3C/svg%3E");
      }

      .flag-icon-de {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 5 3'%3E%3Crect fill='%23000' width='5' height='3'/%3E%3Crect fill='%23D00' width='5' height='2' y='1'/%3E%3Crect fill='%23FFCE00' width='5' height='1' y='2'/%3E%3C/svg%3E");
      }

      .flag-icon-fr {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 3 2'%3E%3Crect fill='%23002395' width='1' height='2'/%3E%3Crect fill='white' width='1' height='2' x='1'/%3E%3Crect fill='%23ED2939' width='1' height='2' x='2'/%3E%3C/svg%3E");
      }

      .flag-icon-pt {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 400'%3E%3Crect fill='%23006600' width='240' height='400'/%3E%3Crect fill='%23FF0000' width='360' height='400' x='240'/%3E%3C/svg%3E");
      }

      .language-name {
        font-weight: 500;
      }

      .status-indicators {
        min-width: 100px;
        justify-content: flex-end;
      }

      .badge {
        font-size: 0.7rem;
        font-weight: normal;
      }

      .translation-status {
        width: 20px;
        text-align: center;
      }

      .gap-2 {
        gap: 0.5rem;
      }
    `,
  ],
})
export class ModalTranslateLanguagesComponent implements OnInit {
  // Icons
  faLanguage = faLanguage;
  faCheck = faCheck;
  faExclamationTriangle = faExclamationTriangle;
  faSpinner = faSpinner;

  // Modal inputs (set by ModalService)
  title = '';
  content = '';
  data: ModalTranslateLanguagesData = {
    sourceLanguage: 'nl',
    languagesWithContent: [],
  };
  buttons: any[] = [];

  // State
  private selectedLanguages = signal<Set<TranslationLanguage>>(new Set());
  isTranslating = signal<boolean>(false);

  // Computed
  availableLanguages = computed(() => {
    return TRANSLATION_LANGUAGES.filter(
      (lang) => lang.code !== this.data.sourceLanguage
    );
  });

  selectedCount = computed(() => this.selectedLanguages().size);

  allSelected = computed(
    () => this.selectedCount() === this.availableLanguages().length
  );

  someSelected = computed(() => this.selectedCount() > 0);

  constructor(public activeModal: NgbActiveModal) {}

  ngOnInit(): void {
    // Pre-select languages if provided
    if (this.data.preSelectedLanguages?.length) {
      const preSelected = new Set<TranslationLanguage>(
        this.data.preSelectedLanguages.filter(
          (lang) => lang !== this.data.sourceLanguage
        )
      );
      this.selectedLanguages.set(preSelected);
    }
  }

  /**
   * Get language display name
   */
  getLanguageName(code: TranslationLanguage): string {
    const lang = TRANSLATION_LANGUAGES.find((l) => l.code === code);
    return lang?.name || code.toUpperCase();
  }

  /**
   * Check if language is selected
   */
  isSelected(code: TranslationLanguage): boolean {
    return this.selectedLanguages().has(code);
  }

  /**
   * Toggle language selection
   */
  toggleLanguage(code: TranslationLanguage): void {
    const current = new Set(this.selectedLanguages());
    if (current.has(code)) {
      current.delete(code);
    } else {
      current.add(code);
    }
    this.selectedLanguages.set(current);
  }

  /**
   * Toggle select all languages
   */
  toggleSelectAll(): void {
    if (this.allSelected()) {
      this.selectedLanguages.set(new Set());
    } else {
      const allCodes = this.availableLanguages().map(
        (l) => l.code
      ) as TranslationLanguage[];
      this.selectedLanguages.set(new Set(allCodes));
    }
  }

  /**
   * Check if language has existing content
   */
  hasExistingContent(code: TranslationLanguage): boolean {
    return this.data.languagesWithContent?.includes(code) ?? false;
  }

  /**
   * Check if any selected languages have existing content
   */
  hasSelectedLanguagesWithContent(): boolean {
    return Array.from(this.selectedLanguages()).some((code) =>
      this.hasExistingContent(code)
    );
  }

  /**
   * Get names of selected languages that have existing content
   */
  getSelectedLanguagesWithContentNames(): string {
    const languagesWithContent = Array.from(this.selectedLanguages())
      .filter((code) => this.hasExistingContent(code))
      .map((code) => this.getLanguageName(code));
    return languagesWithContent.join(', ');
  }

  /**
   * Get translation status for a language
   */
  getTranslationStatus(code: TranslationLanguage): TranslationStatus | null {
    if (!this.data.showProgress || !this.data.translationProgress) {
      return null;
    }
    const progress = this.data.translationProgress.find(
      (p) => p.language === code
    );
    return progress?.status || null;
  }

  /**
   * Handle cancel button click
   */
  onCancel(): void {
    const result: ModalTranslateLanguagesResult = {
      confirmed: false,
      selectedLanguages: [],
      overwriteConfirmed: false,
    };
    this.activeModal.close(result);
  }

  /**
   * Handle confirm button click
   */
  onConfirm(): void {
    const selectedArray = Array.from(
      this.selectedLanguages()
    ) as TranslationLanguage[];
    const result: ModalTranslateLanguagesResult = {
      confirmed: true,
      selectedLanguages: selectedArray,
      overwriteConfirmed: this.hasSelectedLanguagesWithContent(),
    };
    this.activeModal.close(result);
  }

  /**
   * Set translating state (called from parent during translation)
   */
  setTranslating(translating: boolean): void {
    this.isTranslating.set(translating);
  }

  /**
   * Update translation progress (called from parent)
   */
  updateProgress(progress: TranslationProgress[]): void {
    this.data = {
      ...this.data,
      showProgress: true,
      translationProgress: progress,
    };
  }
}
